import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User, UserSession, TelegramWebApp } from '../types';
import apiService from '../services/api';

interface AuthState {
  // State
  user: User | null;
  session: UserSession | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  telegramWebApp: TelegramWebApp | null;
  hasCompletedOnboarding: boolean;

  // Actions
  setTelegramWebApp: (webApp: TelegramWebApp) => void;
  authenticate: (initData: string) => Promise<void>;
  loadUserProfile: () => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  handleSessionExpired: () => void;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  completeOnboarding: () => void;
  resetOnboarding: () => Promise<void>;
  updateOnboardingStep: (step: number) => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      session: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      telegramWebApp: null,
      hasCompletedOnboarding: false,

      // Actions
      setTelegramWebApp: (webApp: TelegramWebApp) => {
        set({ telegramWebApp: webApp });
      },

      authenticate: async (initData: string) => {
        set({ isLoading: true, error: null });
        
        try {
          const authResponse = await apiService.authenticateWithTelegram(initData);
          
          console.log('🔐 authStore: получены данные пользователя', {
            user: authResponse.user,
            onboarding_step: authResponse.user.onboarding_step,
            onboarding_completed: authResponse.user.onboarding_completed,
          });
          
          set({
            user: authResponse.user,
            session: authResponse.session,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            hasCompletedOnboarding: authResponse.user.onboarding_completed || false,
          });
        } catch (error: any) {
          set({
            error: error.message || 'Authentication failed',
            isLoading: false,
            isAuthenticated: false,
          });
          throw error;
        }
      },

      loadUserProfile: async () => {
        if (!get().isAuthenticated) {
          throw new Error('Not authenticated');
        }

        set({ isLoading: true, error: null });
        
        try {
          const user = await apiService.getUserProfile();
          
          set({
            user,
            isLoading: false,
            error: null,
            hasCompletedOnboarding: user.onboarding_completed || false,
          });
        } catch (error: any) {
          set({
            error: error.message || 'Failed to load user profile',
            isLoading: false,
          });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true, error: null });
        
        try {
          await apiService.logout();
        } catch (error) {
          // Ignore logout errors, just clear local state
          console.warn('Logout error:', error);
        } finally {
          set({
            user: null,
            session: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      },

      refreshSession: async () => {
        if (!get().session) {
          throw new Error('No session to refresh');
        }

        set({ isLoading: true, error: null });
        
        try {
          const session = await apiService.refreshSession();
          
          set({
            session,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          set({
            error: error.message || 'Failed to refresh session',
            isLoading: false,
          });
          throw error;
        }
      },

      handleSessionExpired: () => {
        console.log('🔄 Handling session expiration - clearing auth state');
        set({
          user: null,
          session: null,
          isAuthenticated: false,
          error: 'Session expired',
        });
      },

      clearError: () => {
        set({ error: null });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      completeOnboarding: () => {
        set({ hasCompletedOnboarding: true });
        // Принудительный редирект на главную страницу после завершения онбординга
        window.location.href = '/';
      },

      resetOnboarding: async () => {
        try {
          // Call API to reset onboarding on server
          const result = await apiService.resetOnboarding();
          
          // Update local state
          const user = get().user;
          if (user) {
            set({
              user: {
                ...user,
                onboarding_step: result.onboarding_step,
                onboarding_completed: result.onboarding_completed,
              },
              hasCompletedOnboarding: false,
            });
          } else {
            set({ hasCompletedOnboarding: false });
          }
        } catch (error: any) {
          console.error('Failed to reset onboarding:', error);
          // Still reset local state even if API fails
        set({ hasCompletedOnboarding: false });
        }
      },

      updateOnboardingStep: async (step: number) => {
        try {
          const result = await apiService.updateOnboardingProgress(step);
          
          // Обновляем пользователя с новыми данными прогресса
          const user = get().user;
          if (user) {
            set({
              user: {
                ...user,
                onboarding_step: result.onboarding_step,
                onboarding_completed: result.onboarding_completed,
              },
              hasCompletedOnboarding: result.onboarding_completed,
            });
          }
        } catch (error: any) {
          console.error('Failed to update onboarding step:', error);
          // Не выбрасываем ошибку, чтобы не блокировать переход на следующий шаг
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        session: state.session,
        // НЕ сохраняем isAuthenticated - при каждом обновлении страницы будет ре-аутентификация
        // isAuthenticated: state.isAuthenticated,
        hasCompletedOnboarding: state.hasCompletedOnboarding,
      }),
    }
  )
); 